import React, { useState, useEffect } from "react";
import API from "../api/api"; // Make sure this has baseURL set to /api/v1/
import "./Settings.css";

function passwordStrength(value) {
  if (!value || value.length === 0) return { score: 0, label: "Very weak" };
  let score = 0;
  if (value.length >= 8) score += 1;
  if (/[A-Z]/.test(value)) score += 1;
  if (/[0-9]/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value)) score += 1;
  if (score <= 1) return { score, label: "Weak" };
  if (score === 2) return { score, label: "Okay" };
  if (score === 3) return { score, label: "Good" };
  return { score, label: "Strong" };
}

export default function Settings() {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [fieldErrors, setFieldErrors] = useState(null);

  useEffect(() => {
    if (!message) return;
    let timer;
    if (message.type === "success") {
      timer = setTimeout(() => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/admin/login";
      }, 2000);
    } else {
      timer = setTimeout(() => {
        setMessage(null);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [message]);

  const strength = passwordStrength(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors(null);

    if (!oldPassword || !newPassword || !confirmPassword) {
      setMessage({ type: "error", text: "All fields are required." });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({
        type: "error",
        text: "New password and confirmation do not match.",
      });
      return;
    }

    setLoading(true);

    try {
      await API.post("auth/change-password/", {
        old_password: oldPassword,
        new_password: newPassword,
      });

      setMessage({ type: "success", text: "Password updated! Redirecting..." });
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const data = err?.response?.data;
      if (data?.detail) {
        setMessage({ type: "error", text: data.detail });
      } else if (data?.new_password || data?.password) {
        const errors = data.new_password || data.password;
        setFieldErrors(errors);
        setMessage({
          type: "error",
          text: Array.isArray(errors) ? errors.join(" ") : String(errors),
        });
      } else {
        setMessage({ type: "error", text: "Failed to update password" });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Settings</h1>
      </div>
      <div className="settings-body">
        <main className="settings-content">
          {message && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}
          <section className="card change-password-card">
            <form onSubmit={handleSubmit} className="password-form">
              <label className="field">
                <span>Old Password</span>
                <div className="input-inline">
                  <input
                    type={showOld ? "text" : "password"}
                    value={oldPassword}
                    autoComplete="current-password"
                    onChange={(e) => setOldPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowOld((v) => !v)}
                  >
                    {showOld ? "Hide" : "Show"}
                  </button>
                </div>
              </label>
              <label className="field">
                <span>New Password</span>
                <div className="input-inline">
                  <input
                    type={showNew ? "text" : "password"}
                    value={newPassword}
                    autoComplete="new-password"
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew((v) => !v)}
                  >
                    {showNew ? "Hide" : "Show"}
                  </button>
                </div>
                <div className="strength">
                  <div className={`bar s-${strength.score}`} />
                  <div className="strength-label">{strength.label}</div>
                </div>
              </label>
              <label className="field">
                <span>Confirm Password</span>
                <div className="input-inline">
                  <input
                    type={showConfirm ? "text" : "password"}
                    value={confirmPassword}
                    autoComplete="new-password"
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                  >
                    {showConfirm ? "Hide" : "Show"}
                  </button>
                </div>
              </label>
              {fieldErrors && (
                <ul className="field-errors">
                  {Array.isArray(fieldErrors)
                    ? fieldErrors.map((e, i) => <li key={i}>{e}</li>)
                    : <li>{fieldErrors}</li>}
                </ul>
              )}
              <div className="form-actions">
                <button type="submit" disabled={loading}>
                  {loading ? "Updating..." : "Update Password"}
                </button>
              </div>
            </form>
          </section>
        </main>
      </div>
    </div>
  );
}